type Row = {
  value: any
  fieldValueId: string
  parentFieldValueId?: string
}

type FieldArray = Row[]
type GroupedColumn = Record<string, FieldArray>
type FormValues = Record<string, any>

type Column = {
  groupedBy: string[]
  fieldLinkId: string
}

export type Node = [fieldValueId: string, rows: Level | null]

export type Level = {
  fieldLinkId: string
  rows: Node[]
}

const buildGroupedByMap = (columns: Column[]) => {
  const map: Record<string, string[]> = {}

  for (const c of columns) {
    map[c.fieldLinkId] = c.groupedBy ?? []
  }
  return map
}

export const buildGetTree = (formValues: FormValues, columns: Column[]) => {
  const groupedByMap = buildGroupedByMap(columns)

  const pickParent = (curFieldLinkId: string, subFieldLinkId: string | undefined): string | null => {
    const parents = groupedByMap[curFieldLinkId] ?? []

    if (!parents.length) {
      return null
    }

    if (parents.length === 1) {
      return parents[0]
    }

    return subFieldLinkId
  }

  const getChainToRoot = (targetFieldLinkId: string, subFieldLinkId?: string): string[] => {
    const chain: string[] = []

    let currentFieldLink: string | null = targetFieldLinkId

    while (currentFieldLink) {
      chain.push(currentFieldLink)
      currentFieldLink = pickParent(currentFieldLink, subFieldLinkId)
    }

    return chain.reverse()
  }

  const buildLevel = (chain: string[], levelIndex: number, parentValueId: string): Level | null => {
    if (levelIndex >= chain.length) {
      return null
    }

    const fieldLinkId = chain[levelIndex]
    const grouped = formValues[fieldLinkId] as GroupedColumn

    const rows: FieldArray = grouped?.[parentValueId] ?? []

    return {
      fieldLinkId,
      rows: rows.map((r): Node => [r.fieldValueId, buildLevel(chain, levelIndex + 1, r.fieldValueId)])
    }
  }

  const getTree = (targetFieldLinkId: string, subFieldLinkId?: string): Level => {
    const chain = getChainToRoot(targetFieldLinkId, subFieldLinkId)
    const rootFieldLinkId = chain[0] ?? targetFieldLinkId

    const rootRows: FieldArray = (formValues[rootFieldLinkId] as FieldArray) ?? []

    return {
      fieldLinkId: rootFieldLinkId,
      rows: rootRows.map((r): Node => [r.fieldValueId, buildLevel(chain, 1, r.fieldValueId)])
    }
  }

  return { getTree }
}
